import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';
import '../styles/LoginPage.css';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(null);
  const [setupKey, setSetupKey] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    authService.needsSetup().then(setNeedsSetup).catch(() => setNeedsSetup(false));
  }, []);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const authData = await authService.setup(setupKey, email, password, confirmPassword);
      // Inyectar en el contexto y redirigir
      const { token, user, roleLabel } = authData;
      localStorage.setItem('rxapp_auth', JSON.stringify({ token, user, roleLabel }));
      localStorage.setItem('token', token);
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.message || 'Error en la configuración');
    } finally {
      setLoading(false);
    }
  };

  if (needsSetup === null) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <img src="/images/Logotipo-RX-big.png" alt="RxApp" className="login-logo-img" />
            <p className="login-logo-text">RedXtreme Platform</p>
          </div>
          <div className="login-form" style={{ padding: '48px 24px' }}>
            <div className="loading-spinner mx-auto mb-4" style={{ width: 40, height: 40 }}></div>
            <p className="text-center text-secondary">Verificando configuración...</p>
          </div>
        </div>
      </div>
    );
  }

  if (needsSetup) {
    return (
      <div className="login-page">
        <div className="login-container login-setup">
          <div className="login-header">
            <img src="/images/Logotipo-RX-big.png" alt="RxApp" className="login-logo-img" />
            <p className="login-logo-text">RedXtreme Platform</p>
            <h1>Configuración Inicial</h1>
            <p>Primera vez: crea la cuenta de Super Administrador</p>
          </div>

          <form className="login-form" onSubmit={handleSetupSubmit}>
            <h2>Crear Super Administrador</h2>
            <p className="login-setup-hint">
              Ingresa la clave de configuración definida en el servidor (RXAPP_SETUP_SECRET)
            </p>

            {error && (
              <div className="login-error">{error}</div>
            )}

            <div className="form-group">
              <label htmlFor="setupKey">Clave de configuración *</label>
              <input
                id="setupKey"
                type="password"
                value={setupKey}
                onChange={(e) => setSetupKey(e.target.value)}
                placeholder="Clave de setup"
                required
                disabled={loading}
                autoComplete="off"
              />
            </div>

            <div className="form-group">
              <label htmlFor="setup-email">Email *</label>
              <input
                id="setup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@tudominio.com"
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="setup-password">Contraseña *</label>
              <input
                id="setup-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                required
                minLength={6}
                disabled={loading}
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar contraseña *</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la contraseña"
                required
                minLength={6}
                disabled={loading}
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading}
            >
              {loading ? 'Creando cuenta...' : 'Crear Super Administrador'}
            </button>
          </form>

          <div className="login-footer">
            <p>© 2025 RedXtreme - RX-App v1.0.0</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img src="/images/Logotipo-RX-big.png" alt="RxApp" className="login-logo-img" />
          <p className="login-logo-text">RedXtreme Platform</p>
        </div>

        <form className="login-form" onSubmit={handleLoginSubmit}>
          <h2>Iniciar Sesión</h2>

          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              required
              autoComplete="email"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </form>

        <div className="login-footer">
          <p>© 2025 RedXtreme - RX-App v1.0.0</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
